import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertTriangle, ShieldCheck, ClipboardCheck, Wrench } from "lucide-react";
import { DIVISION_COLUMNS } from "@/components/torq/stationData";

const List = ({ title, items, icon: Icon, testId }) =>
  items && items.length ? (
    <div data-testid={testId}>
      <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-3 flex items-center gap-2">
        {Icon && <Icon size={12} />} {title}
      </p>
      <ul className="space-y-2 text-sm text-[#a3a39f] leading-relaxed">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3">
            <span className="font-mono text-[#454540] shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

export default function StationModal({ station, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.documentElement.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = "";
      window.__lenis?.start();
    };
  }, [onClose]);

  const s = station;
  const settings = s.settings || {};

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[70] bg-[#161614]/90 backdrop-blur-md flex items-start sm:items-center justify-center p-0 sm:p-6"
        onClick={onClose}
        data-testid="station-modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-label={`Station ${s.n} — ${s.name}`}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-3xl max-h-[100svh] sm:max-h-[90vh] overflow-y-auto bg-[#1c1c1a] border border-[#2e2e2a]"
          onClick={(e) => e.stopPropagation()}
          data-testid="station-modal"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 lg:px-10 h-16 bg-[#1c1c1a]/95 backdrop-blur border-b border-[#2e2e2a]">
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a]">STATION {s.n} / 07</p>
            <button
              onClick={onClose}
              data-testid="station-modal-close"
              aria-label="Close station details"
              className="p-3 min-w-[48px] min-h-[48px] flex items-center justify-center text-[#a3a39f] hover:text-[#f0eee4] transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="px-6 lg:px-10 py-8 space-y-10 pb-28 sm:pb-10">
            <div>
              <h3 className="font-display font-extrabold uppercase leading-[0.9] text-4xl lg:text-6xl" data-testid="station-modal-name">
                {s.name}
              </h3>
              {s.tag && (
                <p className="mt-2 font-mono text-[11px] tracking-[0.25em] text-[#a3a39f]">{s.tag}</p>
              )}
            </div>

            {s.image ? (
              <figure data-testid="station-modal-figure">
                <div className="overflow-hidden border border-[#2e2e2a] aspect-video">
                  <img src={s.image} alt={s.image_alt || s.name} loading="lazy" className="w-full h-full object-cover grayscale contrast-125" data-testid="station-modal-image" />
                </div>
                <figcaption className="mt-2 font-mono text-[10px] tracking-[0.2em] text-[#6e6e6a]">
                  DEMO IMAGE — {s.name}
                </figcaption>
              </figure>
            ) : (
              <div className="border border-dashed border-[#454540] aspect-video flex items-center justify-center" data-testid="station-modal-image-placeholder">
                <p className="font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] text-center px-6">
                  DEMO IMAGE COMING SOON — STATION {s.n}
                </p>
              </div>
            )}

            {s.purpose && (
              <div data-testid="station-modal-purpose">
                <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-3">PURPOSE</p>
                <p className="text-sm text-[#a3a39f] leading-relaxed">{s.purpose}</p>
                {s.description && <p className="mt-3 text-base text-[#f0eee4] leading-relaxed">{s.description}</p>}
              </div>
            )}

            <List title="STEP-BY-STEP" items={s.instructions} icon={ClipboardCheck} testId="station-modal-instructions" />

            {(s.valid?.length || s.invalid?.length) > 0 && (
              <div className="grid sm:grid-cols-2 gap-6" data-testid="station-modal-standards">
                <List title="VALID REP" items={s.valid} icon={ShieldCheck} testId="station-modal-valid" />
                <List title="NO-REP" items={s.invalid} icon={AlertTriangle} testId="station-modal-invalid" />
              </div>
            )}
            <List title="MOVEMENT STANDARDS" items={s.standards} testId="station-modal-standards-list" />
            <List title="COMMON MISTAKES" items={s.mistakes} testId="station-modal-mistakes" />
            <List title="SAFETY" items={s.safety} testId="station-modal-safety" />
            <List title="JUDGING & COMPLETION" items={s.judging} testId="station-modal-judging" />

            <div className="border border-[#2e2e2a] p-5" data-testid="station-modal-settings">
              <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-4 flex items-center gap-2">
                <Wrench size={12} /> EQUIPMENT & OFFICIAL SETTINGS
              </p>
              <div className="font-mono text-[11px] tracking-[0.1em] space-y-2">
                <div className="flex justify-between gap-4"><span className="text-[#6e6e6a]">EQUIPMENT</span><span className="text-right">{s.equipment || "—"}</span></div>
                <div className="flex justify-between gap-4"><span className="text-[#6e6e6a]">WEIGHT</span><span className="text-right">{settings.weight || "—"}</span></div>
                <div className="flex justify-between gap-4"><span className="text-[#6e6e6a]">DISTANCE</span><span className="text-right">{settings.distance || "—"}</span></div>
                <div className="flex justify-between gap-4"><span className="text-[#6e6e6a]">REPS</span><span className="text-right">{settings.reps || "—"}</span></div>
              </div>
              {s.standards && (
                <div className="mt-5 pt-4 border-t border-[#2e2e2a]" data-testid="station-modal-division-standards">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-3">STANDARDS BY DIVISION</p>
                  <div className="font-mono text-[11px] tracking-[0.05em] space-y-2">
                    {DIVISION_COLUMNS.map(([k, lbl]) => (
                      <div key={k} className="flex justify-between gap-4">
                        <span className="text-[#6e6e6a]">{lbl}</span>
                        <span className="text-right text-[#f0eee4]">{s.standards[k] || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {settings.provisional && (
                <p className="mt-4 font-mono text-[10px] tracking-[0.15em] text-[#d8a24a] border border-[#d8a24a]/40 px-3 py-2" data-testid="station-modal-provisional">
                  PROVISIONAL — official weights, distances and rep counts are confirmed by the organiser before race day.
                </p>
              )}
            </div>
          </div>

          <div className="sticky bottom-0 sm:hidden px-6 py-4 bg-[#1c1c1a]/95 backdrop-blur border-t border-[#2e2e2a]">
            <button
              onClick={onClose}
              data-testid="station-modal-back"
              className="btn-solid w-full border border-[#454540] font-mono text-xs tracking-[0.25em] py-4 hover:border-[#f0eee4] transition-colors"
            >
              BACK TO STATIONS
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
